import React, { useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Routes from './Routes';
import './Vendor';
import './styles/bootstrap.scss';
import './styles/app.scss';
import * as CONST from './components/Common/constants';
import { createTheme, ThemeProvider } from '@material-ui/core/styles';
import ReactGA from 'react-ga';
import { IntlProvider, addLocaleData } from 'react-intl';
import en from 'react-intl/locale-data/en';
import pt from 'react-intl/locale-data/pt';
import localeData from './server/locales/data.json';
import Moment from 'moment';
import 'moment/locale/pt-br';
import { LocalizationProvider } from '@material-ui/pickers';
import MomentAdapter from '@material-ui/pickers/adapter/moment';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import { checkAppVersion } from './utils/versionControl';

addLocaleData([...en, ...pt]);

export default function App() {
  // TODO: É necessário identificar uma forma de pegar a linguagem do perfil do usuário sem usar useSelector aqui. Ele faz com que cause re-render na aplicação.
  const [userLang, setUserLang] = useState('');
  const user = useSelector((state) => state.user.user);

  /** Verifica se a versão do app mudou e força o reload */
  useEffect(() => {
    const versionChanged = checkAppVersion();
    if (versionChanged) window.location.href = '/login';
  }, []);

  useEffect(() => {
    function loadUser() {
      setUserLang(user.language);
    }
    loadUser();
  }, [user]);

  // Dev console spam (react-intl + Material-UI) was producing 30k+ lines with stacks and
  // contributing to main-thread freezes on large Pipeline renders (itrecruiter localhost).
  useEffect(() => {
    const shouldMute = (...args) => {
      const text = args
        .map((a) => (typeof a === 'string' ? a : a && a.message ? String(a.message) : ''))
        .join(' ');
      if (!text) return false;
      return (
        text.indexOf('[React Intl]') !== -1 ||
        text.indexOf('Missing message:') !== -1 ||
        text.indexOf('Material-UI') !== -1 ||
        text.indexOf('unique "key" prop') !== -1 ||
        text.indexOf('Failed prop type') !== -1 ||
        text.indexOf('React does not recognize the') !== -1 ||
        text.indexOf('non-boolean attribute') !== -1 ||
        text.indexOf('no-unused-vars') !== -1 ||
        text.indexOf('no-extend-native') !== -1 ||
        text.indexOf('no-useless-constructor') !== -1 ||
        text.indexOf('webpackHotDevClient') !== -1 ||
        text.indexOf('There were more warnings in other files') !== -1
      );
    };
    const originalError = console.error;
    const originalWarn = console.warn;
    console.error = (...args) => {
      if (shouldMute(...args)) return;
      originalError.apply(console, args);
    };
    console.warn = (...args) => {
      if (shouldMute(...args)) return;
      originalWarn.apply(console, args);
    };
    return () => {
      console.error = originalError;
      console.warn = originalWarn;
    };
  }, []);

  const language =
    userLang ||
    (navigator.languages && navigator.languages[0]) ||
    navigator.language ||
    navigator.userLanguage;

  const languageWithoutRegionCode = language.split(/[_-]+/)[0];

  // Try full locale, try locale without region code, fallback to 'en'
  const messages = userLang
    ? localeData[userLang.toLowerCase()]
    : localeData[languageWithoutRegionCode.toLowerCase()] ||
      localeData[language.toLowerCase()] ||
      localeData.en;

  Moment.locale(language);

  // Analytics adds multi-second `collect` noise on localhost Finish timings.
  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  if (!isLocalhost) {
    ReactGA.initialize('UA-134660783-4');
    ReactGA.pageview(window.location.pathname + window.location.search);
  }

  const theme = createTheme(CONST.MATERIAL_THEME);

  return (
    <IntlProvider locale={language} messages={messages}>
      <LocalizationProvider dateAdapter={MomentAdapter} dateLibInstance={Moment} locale={language}>
        <BrowserRouter basename={'/'}>
          <ErrorBoundary>
            <ThemeProvider theme={theme}>
              <Routes />
            </ThemeProvider>
          </ErrorBoundary>
        </BrowserRouter>
      </LocalizationProvider>
    </IntlProvider>
  );
}
